import type { CurrentSession } from '@nlc/contracts';
import { useEffect, useSyncExternalStore } from 'react';

import { isInProgress, type QueuedUpload, type UploadStorage } from '../../api/upload-queue.js';
import {
  discardUpload,
  retryAllUploads,
  retryUpload,
  subscribeUploads,
  uploadQueueFor,
  uploadScopeOf,
} from '../../api/uploads.js';

export type UploadQueueView = {
  entries: readonly QueuedUpload[];
  storage: UploadStorage;
  /** Entries a tap can retry right now (not already running). */
  retryable: number;
  failed: number;
  retry: (id: string) => void;
  retryAll: () => void;
  discard: (id: string) => void;
};

/** Live view of the signed-in member's upload queue for the active organization. */
export function useUploadQueue(session: CurrentSession): UploadQueueView {
  const queue = uploadQueueFor(uploadScopeOf(session));
  const entries = useSyncExternalStore(subscribeUploads, queue.snapshot);
  const storage = useSyncExternalStore(subscribeUploads, queue.storage);

  useEffect(() => {
    void queue.ready().then(() => queue.purgeExpired());
  }, [queue]);

  return {
    entries,
    storage,
    retryable: entries.filter((entry) => !isInProgress(entry)).length,
    failed: entries.filter((entry) => entry.phase === 'failed').length,
    retry: (id) => void retryUpload(queue, id),
    retryAll: () => void retryAllUploads(queue),
    discard: (id) => void discardUpload(queue, id),
  };
}
